/**
 * WebAuthn browser helpers for biometric registration and authentication.
 * Handles communication between browser's Credential Management API and our backend.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Check if WebAuthn is supported by the browser
 */
export function isWebAuthnSupported() {
  return !!(navigator.credentials && window.PublicKeyCredential);
}

/**
 * Check if platform authenticator (fingerprint/face) is available
 */
export async function isPlatformAuthenticatorAvailable() {
  if (!isWebAuthnSupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Convert base64url string to ArrayBuffer
 */
function base64urlToBuffer(base64url) {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0)).buffer;
}

/**
 * Convert ArrayBuffer to base64url string
 */
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Register a new WebAuthn credential (from profile page)
 */
export async function registerBiometric(accessToken) {
  // 1. Get registration options from server
  const optionsRes = await fetch(`${API_URL}/api/auth/webauthn/register-options`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
  
  if (!optionsRes.ok) throw new Error('Failed to get registration options');
  const options = await optionsRes.json();

  // 2. Convert base64url fields to ArrayBuffer for browser API
  const publicKeyOptions = {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64urlToBuffer(options.user.id),
    },
    excludeCredentials: (options.excludeCredentials || []).map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  };

  // 3. Create credential via browser (triggers biometric prompt)
  const credential = await navigator.credentials.create({ publicKey: publicKeyOptions });

  // 4. Convert response for server
  const credentialJSON = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferToBase64url(credential.response.attestationObject),
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
    },
  };

  // 5. Send to server for verification
  const verifyRes = await fetch(`${API_URL}/api/auth/webauthn/register-complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ credential: credentialJSON }),
  });

  if (!verifyRes.ok) {
    const err = await verifyRes.json();
    throw new Error(err.detail || 'Registration verification failed');
  }

  return await verifyRes.json();
}

/**
 * Authenticate with WebAuthn (from lock screen - no token needed)
 */
export async function authenticateBiometric(email) {
  // 1. Get authentication options
  const optionsRes = await fetch(`${API_URL}/api/auth/webauthn/auth-options`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!optionsRes.ok) {
    const err = await optionsRes.json();
    throw new Error(err.detail || 'Failed to get auth options');
  }
  const options = await optionsRes.json();

  // 2. Convert for browser API
  const publicKeyOptions = {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  };

  // 3. Get assertion via browser (triggers biometric prompt)
  const assertion = await navigator.credentials.get({ publicKey: publicKeyOptions });

  // 4. Convert response
  const assertionJSON = {
    id: assertion.id,
    rawId: bufferToBase64url(assertion.rawId),
    type: assertion.type,
    response: {
      authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
      clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
      signature: bufferToBase64url(assertion.response.signature),
      userHandle: assertion.response.userHandle
        ? bufferToBase64url(assertion.response.userHandle)
        : null,
    },
  };

  // 5. Verify on server and get new tokens
  const verifyRes = await fetch(`${API_URL}/api/auth/webauthn/auth-complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, credential: assertionJSON }),
  });

  if (!verifyRes.ok) {
    const err = await verifyRes.json();
    throw new Error(err.detail || 'Biometric authentication failed');
  }

  return await verifyRes.json();
}

/**
 * Check if user has registered biometric credentials
 */
export async function hasBiometricCredentials(email) {
  try {
    const res = await fetch(`${API_URL}/api/auth/webauthn/has-credentials`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    return data.has_credentials;
  } catch {
    return false;
  }
}
